import { ReactNode } from 'react';
import { createPortal } from 'react-dom';
import { Loader2, X } from 'lucide-react';
import { cn } from '@/lib/utils';

interface SandboxModalProps {
	isOpen: boolean;
	onClose: () => void;
	title: ReactNode;
	description?: ReactNode;
	icon?: ReactNode;
	children: ReactNode;
	/** Rótulo do botão principal; sem ele o rodapé mostra só "Fechar". */
	confirmText?: string;
	onConfirm?: () => void;
	confirmDisabled?: boolean;
	isPending?: boolean;
	size?: 'default' | 'wide';
}

/**
 * Modal simples em portal para as ações do sandbox. Não usa o GlobalModal
 * (ModalDrawer) para o formulário ter controle total do estado e do submit.
 */
const SandboxModal = ({
	isOpen,
	onClose,
	title,
	description,
	icon,
	children,
	confirmText,
	onConfirm,
	confirmDisabled = false,
	isPending = false,
	size = 'default',
}: SandboxModalProps) => {
	if (!isOpen) return null;

	return createPortal(
		<div className="fixed inset-0 z-[10020] flex items-center justify-center p-4">
			<div
				className="absolute inset-0 bg-stone-900/60 backdrop-blur-[2px]"
				onClick={isPending ? undefined : onClose}
			/>
			<div
				className={cn(
					'animate-in zoom-in-95 relative flex max-h-[92vh] w-full flex-col rounded-[2rem] bg-white shadow-2xl duration-200',
					size === 'wide' ? 'max-w-3xl' : 'max-w-xl',
				)}
			>
				<div className="flex items-start justify-between gap-4 px-7 pb-3 pt-6">
					<div className="flex items-start gap-3">
						{icon ? (
							<span className="mt-0.5 flex h-9 w-9 shrink-0 items-center justify-center rounded-2xl bg-stone-900 text-white">
								{icon}
							</span>
						) : null}
						<div>
							<h3 className="text-lg font-black text-stone-900">
								{title}
							</h3>
							{description ? (
								<p className="mt-1 text-xs text-stone-400">
									{description}
								</p>
							) : null}
						</div>
					</div>
					<button
						type="button"
						onClick={onClose}
						disabled={isPending}
						className="rounded-full p-2 text-stone-400 transition-colors hover:bg-stone-100 hover:text-stone-900 disabled:opacity-50"
						aria-label="Fechar"
					>
						<X className="h-4 w-4" />
					</button>
				</div>

				<div className="custom-scrollbar min-h-0 flex-1 overflow-y-auto px-7 py-2">
					{children}
				</div>

				<div className="flex items-center justify-end gap-3 px-7 pb-6 pt-4">
					<button
						type="button"
						onClick={onClose}
						disabled={isPending}
						className="text-sm font-bold text-stone-400 transition-colors hover:text-stone-700 disabled:opacity-50"
					>
						{confirmText ? 'Cancelar' : 'Fechar'}
					</button>
					{confirmText && onConfirm ? (
						<button
							type="button"
							onClick={onConfirm}
							disabled={confirmDisabled || isPending}
							className="inline-flex items-center gap-2 rounded-full bg-stone-900 px-5 py-2.5 text-sm font-bold text-white transition-colors hover:bg-stone-800 disabled:cursor-not-allowed disabled:opacity-60"
						>
							{/* Slot fixo: trocar filho de um span vazio não conflita com
							    extensões que reescrevem os nós de texto do botão. */}
							<span className="flex h-4 w-4 items-center justify-center">
								{isPending ? (
									<Loader2 className="h-4 w-4 animate-spin" />
								) : null}
							</span>
							<span>{confirmText}</span>
						</button>
					) : null}
				</div>
			</div>
		</div>,
		document.body,
	);
};

/* ---------- campos de formulário do sandbox ---------- */

export const sandboxInputClass =
	'w-full rounded-xl border border-stone-200 bg-white px-3 py-2 text-sm text-stone-900 outline-none transition-colors placeholder:text-stone-400 focus:border-stone-900';

export const sandboxSelectClass = cn(sandboxInputClass, 'cursor-pointer');

interface SandboxFieldProps {
	label: string;
	hint?: ReactNode;
	children: ReactNode;
	className?: string;
}

export const SandboxField = ({
	label,
	hint,
	children,
	className,
}: SandboxFieldProps) => (
	<label className={cn('block space-y-1', className)}>
		<span className="text-[11px] font-black uppercase tracking-wide text-stone-400">
			{label}
		</span>
		{children}
		{hint ? (
			<span className="block text-xs text-stone-400">{hint}</span>
		) : null}
	</label>
);

interface SandboxChipToggleProps {
	options: Array<{ key: string; label: string }>;
	selected: string[];
	onToggle: (key: string) => void;
	max?: number;
}

/** Seleção múltipla em chips (temas, abordagens complementares...). */
export const SandboxChipToggle = ({
	options,
	selected,
	onToggle,
	max,
}: SandboxChipToggleProps) => (
	<div className="flex flex-wrap gap-2">
		{options.map((option) => {
			const isOn = selected.includes(option.key);
			const blocked =
				!isOn && max !== undefined && selected.length >= max;
			return (
				<button
					key={option.key}
					type="button"
					onClick={() => onToggle(option.key)}
					disabled={blocked}
					className={cn(
						'rounded-full border px-3 py-1.5 text-xs font-semibold transition-colors disabled:cursor-not-allowed disabled:opacity-40',
						isOn
							? 'border-stone-900 bg-stone-900 text-white'
							: 'border-stone-200 text-stone-600 hover:border-stone-400',
					)}
				>
					{option.label}
				</button>
			);
		})}
	</div>
);

interface SandboxNoticeProps {
	tone?: 'info' | 'warning' | 'success' | 'danger';
	children: ReactNode;
	className?: string;
}

export const SandboxNotice = ({
	tone = 'info',
	children,
	className,
}: SandboxNoticeProps) => (
	<div
		className={cn(
			'rounded-xl px-3 py-2 text-xs font-semibold leading-relaxed',
			tone === 'info' && 'bg-stone-100 text-stone-600',
			tone === 'warning' && 'bg-amber-50 text-amber-800',
			tone === 'success' && 'bg-emerald-50 text-emerald-800',
			tone === 'danger' && 'bg-rose-50 text-rose-700',
			className,
		)}
	>
		{children}
	</div>
);

export { SandboxModal };
